/**
 * Build Your Box — Vanilla JavaScript Controller
 * Custom Shopify OS 2.0 Section
 *
 * Manages box product selection state, UI updates, quantity controls,
 * session persistence, and Ajax cart submission with line-item properties.
 */
(function () {
  'use strict';

  /* ========================================================================
     BuildYourBox Class
     ======================================================================== */
  class BuildYourBox {
    constructor(sectionEl) {
      this.section = sectionEl;
      this.sectionId = sectionEl.dataset.sectionId;

      // Read configuration from JSON script block
      const configEl = sectionEl.querySelector('[data-byb-config]');
      if (!configEl) {
        console.warn('BuildYourBox: Missing configuration block.');
        return;
      }

      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (e) {
        console.error('BuildYourBox: Invalid configuration JSON.', e);
        return;
      }

      // Selection state: Map<variantId (string), SelectionItem>
      this.selections = new Map();

      // Cache DOM elements
      this.cacheDOM();

      // Restore session
      this.restoreSession();

      // Bind events
      this.bindEvents();

      // Initial UI render
      this.updateUI();

      // Show sticky bar
      if (this.stickyBar) {
        requestAnimationFrame(() => {
          this.stickyBar.classList.add('is-visible');
        });
      }
    }

    /* ----------------------------------------------------------------------
       DOM Caching
       ---------------------------------------------------------------------- */
    cacheDOM() {
      this.productGrid = this.section.querySelector('[data-byb-grid]');
      this.productCards = this.section.querySelectorAll('.byb-product-card');
      this.stickyBar = this.section.querySelector('[data-byb-sticky]');
      this.slotsContainer = this.section.querySelector('[data-byb-slots]');
      this.buyBtn = this.section.querySelector('[data-byb-buy]');
      this.totalEl = this.section.querySelector('[data-byb-total]');
      this.savedEl = this.section.querySelector('[data-byb-saved]');
      this.counterEl = this.section.querySelector('[data-byb-counter]');
      this.progressBar = this.section.querySelector('[data-byb-progress-bar]');
      this.progressMsg = this.section.querySelector('[data-byb-progress-msg]');
      this.toast = this.section.querySelector('[data-byb-toast]');
    }

    /* ----------------------------------------------------------------------
       Event Binding
       ---------------------------------------------------------------------- */
    bindEvents() {
      // Add-to-box buttons on product cards
      this.section.addEventListener('click', (e) => {
        const addBtn = e.target.closest('[data-action="add-to-box"]');
        if (addBtn) {
          e.preventDefault();
          const variantId = addBtn.dataset.variantId;
          this.addToBox(variantId);
          return;
        }

        const removeBtn = e.target.closest('[data-action="remove-from-box"]');
        if (removeBtn) {
          e.preventDefault();
          const variantId = removeBtn.dataset.variantId;
          this.removeFromBox(variantId);
          return;
        }

        const qtyBtn = e.target.closest('[data-action="qty-change"]');
        if (qtyBtn) {
          e.preventDefault();
          const variantId = qtyBtn.dataset.variantId;
          const delta = parseInt(qtyBtn.dataset.delta, 10);
          this.updateQuantity(variantId, delta);
          return;
        }

        const buyBtn = e.target.closest('[data-byb-buy]');
        if (buyBtn && !buyBtn.disabled) {
          e.preventDefault();
          this.buyNow();
          return;
        }
      });

      // Variant selector changes
      this.section.addEventListener('change', (e) => {
        const select = e.target.closest('.byb-card__variant-select');
        if (select) {
          this.handleVariantChange(select);
        }
      });

      // Keyboard accessibility for slot removal buttons
      this.section.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          const qtyBtn = e.target.closest('[data-action="qty-change"]');
          if (qtyBtn) {
            e.preventDefault();
            const variantId = qtyBtn.dataset.variantId;
            const delta = parseInt(qtyBtn.dataset.delta, 10);
            this.updateQuantity(variantId, delta);
          }
        }
      });
    }

    /* ----------------------------------------------------------------------
       Variant Change Handler
       ---------------------------------------------------------------------- */
    handleVariantChange(selectEl) {
      const card = selectEl.closest('.byb-product-card');
      if (!card) return;

      const selectedOption = selectEl.options[selectEl.selectedIndex];
      const variantId = selectedOption.value;
      const variantTitle = selectedOption.dataset.title;
      const available = selectedOption.dataset.available === 'true';
      const price = selectedOption.dataset.price;
      const comparePrice = selectedOption.dataset.comparePrice;
      const image = selectedOption.dataset.image;

      // Update card data attributes
      card.dataset.variantId = variantId;
      card.dataset.productAvailable = available.toString();
      card.dataset.productPrice = price;

      if (variantTitle && variantTitle !== 'Default Title') {
        const baseTitle = card.dataset.baseTitle || card.dataset.productTitle;
        card.dataset.baseTitle = baseTitle;
        card.dataset.productTitle = `${baseTitle} - ${variantTitle}`;
      }

      // Update card image if variant has one
      if (image) {
        const imgEl = card.querySelector('.byb-card__image');
        if (imgEl) {
          imgEl.src = image;
          card.dataset.productImage = image;
        }
      }

      // Update price display
      const currentPriceEl = card.querySelector('.byb-card__current-price');
      if (currentPriceEl && price) {
        currentPriceEl.textContent = this.formatMoney(parseInt(price, 10));
      }

      // Update add and quantity buttons variant IDs
      const minusBtn = card.querySelector('.byb-card__qty-btn--minus');
      const plusBtn = card.querySelector('.byb-card__qty-btn--plus');
      if (minusBtn) minusBtn.dataset.variantId = variantId;
      if (plusBtn) plusBtn.dataset.variantId = variantId;

      // Update add button
      const addBtn = card.querySelector('[data-action="add-to-box"]');
      if (addBtn) {
        addBtn.dataset.variantId = variantId;
        if (available) {
          addBtn.disabled = false;
          addBtn.removeAttribute('aria-disabled');
          const textSpan = addBtn.querySelector('.byb-card__btn-text');
          if (textSpan) textSpan.textContent = this.config.addButtonText || '+ ADD TO BOX';
        } else {
          addBtn.disabled = true;
          addBtn.setAttribute('aria-disabled', 'true');
          const textSpan = addBtn.querySelector('.byb-card__btn-text');
          if (textSpan) textSpan.textContent = 'Sold Out';
        }
      }

      // Update badge
      const badge = card.querySelector('.byb-card__badge--sold-out');
      if (badge) {
        badge.style.display = available ? 'none' : '';
      }

      this.updateUI();
    }

    /* ----------------------------------------------------------------------
       Add To Box
       ---------------------------------------------------------------------- */
    addToBox(variantId) {
      const card = this.section.querySelector(`.byb-product-card[data-variant-id="${variantId}"]`);
      if (!card) return;

      // Check availability
      if (card.dataset.productAvailable !== 'true') {
        this.showToast('This product is currently sold out.', 'error');
        return;
      }

      const totalCount = this.getTotalCount();
      const maxItems = this.config.maxItems;
      const maxSameProduct = this.config.maxSameProduct;

      // Check if box is full
      if (totalCount >= maxItems) {
        this.showToast('Your box is full. Remove an item to add another product.', 'warning');
        return;
      }

      // Check if same product limit reached
      const existing = this.selections.get(variantId);
      if (existing && existing.quantity >= maxSameProduct) {
        this.showToast(
          `You can add the same product a maximum of ${maxSameProduct} time${maxSameProduct > 1 ? 's' : ''}.`,
          'warning'
        );
        return;
      }

      // Add or increment
      if (existing) {
        existing.quantity += 1;
        this.selections.set(variantId, existing);
      } else {
        this.selections.set(variantId, {
          variantId: variantId,
          productId: card.dataset.productId,
          title: card.dataset.productTitle,
          image: card.dataset.productImage,
          price: parseInt(card.dataset.productPrice, 10),
          quantity: 1,
        });
      }

      this.saveSession();
      this.updateUI();
    }

    /* ----------------------------------------------------------------------
       Remove From Box
       ---------------------------------------------------------------------- */
    removeFromBox(variantId) {
      if (!this.selections.has(variantId)) return;
      this.selections.delete(variantId);
      this.saveSession();
      this.updateUI();
    }

    /* ----------------------------------------------------------------------
       Update Quantity
       ---------------------------------------------------------------------- */
    updateQuantity(variantId, delta) {
      const item = this.selections.get(variantId);
      if (!item) return;

      const newQty = item.quantity + delta;

      // Prevent exceeding max same product
      if (delta > 0 && newQty > this.config.maxSameProduct) {
        this.showToast(
          `You can add the same product a maximum of ${this.config.maxSameProduct} time${this.config.maxSameProduct > 1 ? 's' : ''}.`,
          'warning'
        );
        return;
      }

      // Prevent exceeding max items total
      if (delta > 0) {
        const totalCount = this.getTotalCount();
        if (totalCount >= this.config.maxItems) {
          this.showToast('Your box is full. Remove an item to add another product.', 'warning');
          return;
        }
      }

      if (newQty <= 0) {
        this.removeFromBox(variantId);
        return;
      }

      item.quantity = newQty;
      this.selections.set(variantId, item);
      this.saveSession();
      this.updateUI();
    }

    /* ----------------------------------------------------------------------
       Get Total Count
       ---------------------------------------------------------------------- */
    getTotalCount() {
      let total = 0;
      this.selections.forEach((item) => {
        total += item.quantity;
      });
      return total;
    }

    /* ----------------------------------------------------------------------
       Update UI (master render)
       ---------------------------------------------------------------------- */
    updateUI() {
      const totalCount = this.getTotalCount();
      const minItems = this.config.minItems;
      const maxItems = this.config.maxItems;
      const isComplete = totalCount >= minItems;

      // 1. Update product card button & quantity selector states
      this.productCards.forEach((card) => {
        const vid = card.dataset.variantId;
        const addBtn = card.querySelector('[data-action="add-to-box"]');
        const qtyWrapper = card.querySelector('[data-card-qty-wrapper]');
        const qtyVal = card.querySelector('[data-card-qty-val]');
        const minusBtn = card.querySelector('.byb-card__qty-btn--minus');
        const plusBtn = card.querySelector('.byb-card__qty-btn--plus');

        const item = this.selections.get(vid);
        const qty = item ? item.quantity : 0;

        card.classList.toggle('is-in-box', qty > 0);

        if (minusBtn) minusBtn.dataset.variantId = vid;
        if (plusBtn) plusBtn.dataset.variantId = vid;
        if (addBtn) addBtn.dataset.variantId = vid;

        if (qty > 0) {
          // Hide Add button, show Quantity selector inside product card
          if (addBtn) addBtn.style.display = 'none';
          if (qtyWrapper) qtyWrapper.style.display = 'flex';
          if (qtyVal) {
            qtyVal.textContent = qty;
            qtyVal.setAttribute('aria-label', `Quantity: ${qty}`);
          }

          // Disable plus button if maxSameProduct reached OR box full
          const isMaxSame = qty >= this.config.maxSameProduct;
          const isMaxTotal = totalCount >= maxItems;
          const plusDisabled = isMaxSame || isMaxTotal;

          if (plusBtn) {
            plusBtn.disabled = plusDisabled;
            if (plusDisabled) {
              plusBtn.setAttribute('aria-disabled', 'true');
            } else {
              plusBtn.removeAttribute('aria-disabled');
            }
          }
        } else {
          // Show Add button, hide Quantity selector inside product card
          if (addBtn) addBtn.style.display = '';
          if (qtyWrapper) qtyWrapper.style.display = 'none';
        }
      });

      // 2. Update progress counter
      if (this.counterEl) {
        this.counterEl.innerHTML = `<span>${totalCount}</span> / ${maxItems} selected`;
      }

      // 3. Update progress bar
      if (this.progressBar) {
        const pct = Math.min((totalCount / maxItems) * 100, 100);
        this.progressBar.style.width = pct + '%';
      }

      // 4. Update progress message
      if (this.progressMsg) {
        if (!this.config.boxAvailable) {
          this.progressMsg.textContent = 'This box is currently out of stock.';
        } else if (totalCount === 0) {
          this.progressMsg.textContent = `Select ${minItems} product${minItems > 1 ? 's' : ''} to build your box.`;
        } else if (totalCount < minItems) {
          const remaining = minItems - totalCount;
          this.progressMsg.textContent = `Select ${remaining} more product${remaining > 1 ? 's' : ''} to complete your box.`;
        } else {
          this.progressMsg.textContent = 'Your box is ready! Click BUY NOW to add to cart.';
        }
      }

      // 5. Update sticky bar slots
      this.renderSlots();

      // 6. Update BUY NOW button
      if (this.buyBtn) {
        if (!this.config.boxAvailable) {
          this.buyBtn.disabled = true;
          this.buyBtn.setAttribute('aria-disabled', 'true');
          const btnText = this.buyBtn.querySelector('.byb-buy-btn__text') || this.buyBtn;
          if (btnText) btnText.textContent = 'SOLD OUT';
        } else {
          this.buyBtn.disabled = !isComplete;
          this.buyBtn.setAttribute('aria-disabled', (!isComplete).toString());
          const btnText = this.buyBtn.querySelector('.byb-buy-btn__text') || this.buyBtn;
          if (btnText && btnText !== this.buyBtn) btnText.textContent = this.config.buyButtonText || 'BUY NOW';
        }
      }

      // 7. Update total price display
      if (this.totalEl) {
        this.totalEl.textContent = this.config.boxPriceFormatted;
      }

      // 8. Calculate and display savings
      if (this.savedEl) {
        let individualTotal = 0;
        this.selections.forEach((item) => {
          individualTotal += item.price * item.quantity;
        });
        const savings = individualTotal - this.config.boxPrice;
        if (savings > 0 && totalCount >= minItems) {
          this.savedEl.textContent = 'You save ' + this.formatMoney(savings);
          this.savedEl.classList.add('is-visible');
        } else {
          this.savedEl.textContent = '\u00A0';
          this.savedEl.classList.remove('is-visible');
        }
      }
    }

    /* ----------------------------------------------------------------------
       Render Sticky Bar Slots
       ---------------------------------------------------------------------- */
    renderSlots() {
      if (!this.slotsContainer) return;

      const maxItems = this.config.maxItems;
      const items = Array.from(this.selections.values());

      // Build slot array: one slot per total quantity unit
      let filledSlots = [];
      items.forEach((item) => {
        for (let i = 0; i < item.quantity; i++) {
          filledSlots.push(item);
        }
      });

      let html = '';

      for (let i = 0; i < maxItems; i++) {
        const item = filledSlots[i];

        if (item) {
          html += `
            <div class="byb-slot-wrapper">
              <div
                class="byb-slot is-filled"
                data-action="qty-change"
                data-variant-id="${item.variantId}"
                data-delta="-1"
                role="button"
                tabindex="0"
                title="${this.escapeHtml(item.title)} — Click to remove"
                aria-label="Remove ${this.escapeHtml(item.title)} from box"
              >
                ${item.image
                  ? `<img src="${item.image}" alt="${this.escapeHtml(item.title)}" class="byb-slot__image" width="56" height="56" loading="lazy">`
                  : `<span class="byb-slot__empty-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                    </span>`
                }
                <span class="byb-slot__remove-badge" aria-hidden="true">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </span>
              </div>
              <span class="byb-slot__label">${this.truncate(item.title, 8)}</span>
            </div>`;
        } else {
          html += `
            <div class="byb-slot-wrapper">
              <div class="byb-slot" aria-label="Empty slot ${i + 1}">
                <span class="byb-slot__empty-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                </span>
              </div>
              <span class="byb-slot__label">Product ${i + 1}</span>
            </div>`;
        }
      }

      this.slotsContainer.innerHTML = html;
    }

    /* ----------------------------------------------------------------------
       Buy Now — Add to Shopify Cart
       ---------------------------------------------------------------------- */
    async buyNow() {
      if (!this.config.boxAvailable) {
        this.showToast('This box is currently out of stock. Please check back later.', 'error');
        return;
      }
      const totalCount = this.getTotalCount();
      if (totalCount < this.config.minItems) {
        const remaining = this.config.minItems - totalCount;
        this.showToast(
          `Please select ${remaining} more product${remaining > 1 ? 's' : ''} to complete your box.`,
          'warning'
        );
        return;
      }

      if (!this.config.boxVariantId) {
        this.showToast('Box product is not configured. Please contact support.', 'error');
        return;
      }

      // Build line-item properties
      const properties = {};
      let index = 1;
      this.selections.forEach((item) => {
        properties[`Item ${index}`] = `${item.title} × ${item.quantity}`;
        properties[`_Item ${index}_Variant ID`] = item.variantId;
        properties[`_item_${index}_product_id`] = item.productId;
        index++;
      });

      properties['_total_items'] = totalCount.toString();

      // Set loading state
      this.setLoading(true);

      try {
        const sectionsToFetch = ['cart-drawer', 'cart-icon-bubble'];
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: [
              {
                id: parseInt(this.config.boxVariantId, 10),
                quantity: 1,
                properties: properties,
              },
            ],
            sections: sectionsToFetch,
            sections_url: window.location.pathname
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.description || errorData.message || 'Failed to add to cart.');
        }

        const cartData = await response.json();

        // Clear selection and session
        this.selections.clear();
        this.clearSession();
        this.updateUI();

        this.showToast('Box added to your cart!', 'success');

        // Open cart drawer
        await this.openCartDrawer(cartData);

      } catch (error) {
        console.error('BuildYourBox: Cart error', error);
        this.showToast(error.message || 'Something went wrong. Please try again.', 'error');
      } finally {
        this.setLoading(false);
      }
    }

    /* ----------------------------------------------------------------------
       Open Cart Drawer / Redirect
       ---------------------------------------------------------------------- */
    async openCartDrawer(cartData) {
      const cartDrawer = document.querySelector('cart-drawer');

      if (cartDrawer) {
        // Crucial fix: remove is-empty class so focus trap targets #CartDrawer instead of null .drawer__inner-empty
        cartDrawer.classList.remove('is-empty');

        if (typeof cartDrawer.renderContents === 'function') {
          try {
            let sections = cartData.sections;
            if (!sections) {
              const cartUrl = window.routes?.cart_url || '/cart';
              const sectionsResponse = await fetch(`${cartUrl}?sections=cart-drawer,cart-icon-bubble`);
              if (sectionsResponse.ok) {
                sections = await sectionsResponse.json();
              }
            }

            if (sections) {
              cartDrawer.renderContents({ sections: sections, id: this.config.boxVariantId });
            } else {
              cartDrawer.open();
            }
          } catch (e) {
            console.warn('BuildYourBox: Could not render cart drawer contents, opening anyway.', e);
            cartDrawer.open();
          }
        } else if (typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }

        // Trigger cart update events for header bubbles and other theme listeners
        document.dispatchEvent(new CustomEvent('cartUpdated', { detail: { cartData } }));
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'build-your-box', cartData });
        }
      } else {
        // No cart drawer — redirect to cart page
        window.location = window.routes?.cart_url || '/cart';
      }
    }

    /* ----------------------------------------------------------------------
       Loading State
       ---------------------------------------------------------------------- */
    setLoading(isLoading) {
      if (this.buyBtn) {
        this.buyBtn.classList.toggle('is-loading', isLoading);
        if (isLoading) {
          this.buyBtn.disabled = true;
          this.buyBtn.setAttribute('aria-disabled', 'true');
        }
      }
    }

    /* ----------------------------------------------------------------------
       Toast Notifications
       ---------------------------------------------------------------------- */
    showToast(message, type = 'info') {
      if (!this.toast) return;

      // Clear existing timeout
      if (this._toastTimeout) {
        clearTimeout(this._toastTimeout);
      }

      // Remove old type classes
      this.toast.classList.remove('byb-toast--error', 'byb-toast--success', 'byb-toast--warning');

      // Set type class
      if (type === 'error') this.toast.classList.add('byb-toast--error');
      if (type === 'success') this.toast.classList.add('byb-toast--success');
      if (type === 'warning') this.toast.classList.add('byb-toast--warning');

      // Set message and show
      this.toast.textContent = message;
      this.toast.setAttribute('role', 'alert');
      this.toast.classList.add('is-visible');

      // Auto-hide after 3.5 seconds
      this._toastTimeout = setTimeout(() => {
        this.toast.classList.remove('is-visible');
      }, 3500);
    }

    /* ----------------------------------------------------------------------
       Session Persistence (sessionStorage)
       ---------------------------------------------------------------------- */
    getSessionKey() {
      return `byb-selection-${this.sectionId}`;
    }

    saveSession() {
      try {
        const data = Array.from(this.selections.entries());
        sessionStorage.setItem(this.getSessionKey(), JSON.stringify(data));
      } catch (e) {
        // sessionStorage may be unavailable (private browsing, etc.)
      }
    }

    restoreSession() {
      try {
        const raw = sessionStorage.getItem(this.getSessionKey());
        if (!raw) return;

        const data = JSON.parse(raw);
        if (!Array.isArray(data)) return;

        // Validate each item still exists in the product grid
        const validVariantIds = new Set();
        this.productCards.forEach((card) => {
          if (card.dataset.productAvailable === 'true') {
            validVariantIds.add(card.dataset.variantId);
          }
        });

        data.forEach(([variantId, item]) => {
          if (validVariantIds.has(variantId) && item && item.quantity > 0) {
            // Clamp quantity to max same product
            item.quantity = Math.min(item.quantity, this.config.maxSameProduct);
            this.selections.set(variantId, item);
          }
        });

        // Ensure total doesn't exceed max items
        let total = this.getTotalCount();
        if (total > this.config.maxItems) {
          // Trim from the end
          const entries = Array.from(this.selections.entries());
          this.selections.clear();
          total = 0;
          for (const [vid, item] of entries) {
            if (total + item.quantity <= this.config.maxItems) {
              this.selections.set(vid, item);
              total += item.quantity;
            } else {
              const remaining = this.config.maxItems - total;
              if (remaining > 0) {
                item.quantity = remaining;
                this.selections.set(vid, item);
                total += remaining;
              }
              break;
            }
          }
        }
      } catch (e) {
        // Silently fail — start with empty selection
      }
    }

    clearSession() {
      try {
        sessionStorage.removeItem(this.getSessionKey());
      } catch (e) {
        // Ignore
      }
    }

    /* ----------------------------------------------------------------------
       Utility: Format Money
       ---------------------------------------------------------------------- */
    formatMoney(cents) {
      const format = this.config.moneyFormat || '₹{{amount}}';
      const amount = (cents / 100).toFixed(2);
      const amountNoDecimals = Math.round(cents / 100).toString();
      const amountWithComma = amountNoDecimals.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');

      return format
        .replace('{{amount_with_comma_separator}}', amountWithComma)
        .replace('{{amount_no_decimals_with_comma_separator}}', amountWithComma)
        .replace('{{amount_no_decimals}}', amountNoDecimals)
        .replace('{{amount}}', amount);
    }

    /* ----------------------------------------------------------------------
       Utility: Escape HTML
       ---------------------------------------------------------------------- */
    escapeHtml(str) {
      if (!str) return '';
      const div = document.createElement('div');
      div.appendChild(document.createTextNode(str));
      return div.innerHTML;
    }

    /* ----------------------------------------------------------------------
       Utility: Truncate String
       ---------------------------------------------------------------------- */
    truncate(str, maxLen) {
      if (!str) return '';
      if (str.length <= maxLen) return str;
      return str.substring(0, maxLen) + '…';
    }
  }

  /* ========================================================================
     Initialize on DOM Ready
     ======================================================================== */
  function init() {
    const sections = document.querySelectorAll('[data-byb-section]');
    sections.forEach((section) => {
      if (!section._bybInstance) {
        section._bybInstance = new BuildYourBox(section);
      }
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on Shopify Theme Editor section events
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => {
      const section = e.target.querySelector('[data-byb-section]');
      if (section) {
        section._bybInstance = new BuildYourBox(section);
      }
    });
  }
})();
